import FormErrorMessage from '@components/FormErrorMessage';
import parse from 'html-react-parser';
import PlacingOrderAction from './components/PlacingOrderAction';
import PlacingOrderForm from './components/PlacingOrderForm/PlacingOrderForm';
import PlacingOrderHeader from '@components/PlacingOrderHeader';
import PlacingOrderSelectingDelivery
  from './components/PlacingOrderSelectingDelivery/PlacingOrderSelectingDelivery';
import PlacingOrderView from '@components/PlacingOrderView';
import WalletIcon from '@assets/icons/wallet.svg';
import WalletPinkIcon from '@assets/icons/wallet-pink-16.svg';
import { ButtonType } from '@components/Button';
import { deleteAddress, getAddresses } from '@api/apiClient';
import { differenceInCalendarDays } from 'date-fns';
import {
  EPlacingOrder,
  IPlacingOrderModalWindowProps,
  TPlacingOrderData,
  TPlacingOrderForm,
  TPlacingOrderFormSelf,
  TUpdateFormValue
} from '@components/modals/PlacingOrderModalWindow/types';
import { formatSumWithCurrency } from '@utils/formatValues';
import { getRegions, getValidation } from '@api/apiClient/address';
import { getShipmentDatesForCN } from '@utils/getChineseDate';
import { Grid } from '@components/Grid';
import { Loader } from '@components/Loader';
import { TextColor } from '@components/Text';
import { TPlacingOrderListItem, TPlacingOrderViewData } from '@components/PlacingOrderView/types';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useProfile } from '@contexts/ProfileContext';
import { useTranslation } from 'react-i18next';
import { IPlacingOrderButton } from '@components/PlacingOrderButtons/types';

const PlacingOrderModalWindow: React.FC<IPlacingOrderModalWindowProps> = (props: IPlacingOrderModalWindowProps): React.ReactElement => {
  const {
    availableCountries,
    costOfService,
    costOfServiceDifferent,
    deliveryType,
    errorSendPlacingOrder,
    errorSendTopUpBalance,
    expandTabs,
    handlerOnClosePlacingOrderProcessing,
    handlerResetSendOrder,
    handlerSetDeliveryType,
    handlerSubmit,
    isErrorSendPlacingOrder,
    isErrorSendTopUpBalance,
    isLoadingSendPlacingOrder,
    isLoadingSendTopUpBalance,
    isNew,
    parcelsAny,
    parcelsGood,
    parcelsGoodTimeslotFrom,
    parcelsGoodWarehouse,
    placingOrderInProcessed,
    placingOrderType,
    allowRu
  } = props;

  const [addressIdToDelete, setAddressIdToDelete] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [currentTypeModalWindow, setCurrentTypeModalWindow] = useState<string>('');
  const [forms, setForms] = useState<TPlacingOrderForm[]>([]);
  const [numberOfSteps, setNumberOfSteps] = useState<number | undefined>(undefined);

  const { account } = useProfile();
  const { t } = useTranslation();

  const variableCurrency: string = account?.region === 'TR' ? '$' : '¥';

  const costOfAction: number =
      account?.region === 'TR'
          ? costOfService
          : [EPlacingOrder.UNCLAIMED_AWAITING].includes(placingOrderType)
              ? deliveryType === 'default' ? costOfService : costOfServiceDifferent!
              : costOfService;

  const {
    data: dataGetAddresses,
    isLoading: isLoadingGetAddresses,
    refetch: refetchGetAddresses
  } = useQuery({
    queryKey: ['getAddresses'],
    queryFn: () => getAddresses(),
    retry: 1
  });

  const {
    data: dataGetRegions,
    isLoading: isLoadingGetRegions
  } = useQuery({
    queryKey: ['getRegions'],
    queryFn: () => getRegions(),
    retry: 1,
    enabled: account!.region === 'CN'
  });

  const {
    data: dataGetValidation,
    isLoading: isLoadingGetValidation,
    mutate: mutateGetValidation,
    reset: resetGetValidation
  } = useMutation({
    mutationFn: getValidation
  });

  const {
    error: errorDeleteAddress,
    isError: isErrorDeleteAddress,
    isLoading: isLoadingDeleteAddress,
    mutate: mutateDeleteAddress,
    reset: resetDeleteAddress
  } = useMutation({
    mutationFn: deleteAddress,
    onSuccess: () => {
      refetchGetAddresses()
          .then(() => {
            setTimeout(() => {
              setCurrentTypeModalWindow('placing');
            }, 300);
          });
    }
  });

  useEffect(() => {
    setCurrentTypeModalWindow(
        account?.region === 'TR'
            ? 'placing'
            : placingOrderType === EPlacingOrder.UNCLAIMED_AWAITING
                ? 'selecting'
                : 'placing'
    );
  }, [account?.region, placingOrderType]);

  useEffect(() => {
    if (dataGetAddresses) {
      const conditionCN: boolean = account!.region === 'CN' && !!dataGetRegions;
      const conditionTR: boolean = account!.region === 'TR';
      const conditionRU: boolean = deliveryType === 'RU';
      const numberDays: number = parcelsGoodTimeslotFrom
          ? differenceInCalendarDays(parcelsGoodTimeslotFrom, new Date())
          : 0;
      if (conditionCN || conditionTR) {
        const uniqueCountries =
            conditionRU
                ? [deliveryType]
                : Array.from(
                    new Set(parcelsGood!.map((item: TPlacingOrderListItem) =>
                            item.countryCode ?? (
                                [EPlacingOrder.RETURN_AWAITING].includes(placingOrderType)
                                    ? conditionTR
                                        ? account!.region
                                        : 'unknown'
                                    : account!.region
                            ))
                    )
                ).sort((a, b) => {
                  if (a === 'CN') return -1;
                  if (b === 'CN') return 1;
                  return a!.localeCompare(b!);
                });
        setNumberOfSteps(uniqueCountries.length + 1);
        const forms: TPlacingOrderForm[] = uniqueCountries
            .filter((country): country is string => country !== undefined)
            .map((country: string, index: number) => {
              return ([
                country,
                {
                  addressTemplates: dataGetAddresses?.addressTemplates,
                  availableCountries,
                  contactHint: ([EPlacingOrder.UNCLAIMED_AWAITING].includes(placingOrderType) && conditionRU) ||
                  [
                    EPlacingOrder.FBP_NEW,
                    EPlacingOrder.PICKUP_READY
                  ]
                      .includes(placingOrderType)
                      ? t(`placingOrderModalWindow.form.contacts.hint.${placingOrderType}`)
                      : undefined,
                  contactMinLength: [
                    EPlacingOrder.FBP_NEW,
                    EPlacingOrder.PICKUP_READY
                  ]
                      .includes(placingOrderType)
                      ? 2
                      : undefined,
                  contactPlaceholder: [
                    EPlacingOrder.FBP_NEW,
                    EPlacingOrder.PICKUP_READY
                  ]
                      .includes(placingOrderType)
                      ? t('placingOrderModalWindow.form.contacts.name')
                      : t('placingOrderModalWindow.form.contacts.fullName'),
                  description: country === 'TR' || country === 'unknown'
                      ? undefined
                      : [
                        EPlacingOrder.PROBLEMATIC_CUSTOMS,
                        EPlacingOrder.PROBLEMATIC_FIRST_MILE,
                        EPlacingOrder.RETURN_AWAITING,
                        !conditionRU && EPlacingOrder.UNCLAIMED_AWAITING,
                        EPlacingOrder.UNCLAIMED_UNPAID
                      ]
                          .includes(placingOrderType)
                          ? parse(t(`placingOrderModalWindow.description.${country}.${placingOrderType}.${index ? 'anyStep' : 'firstStep'}`))
                          : [
                            EPlacingOrder.PICKUP_READY,
                            conditionRU && EPlacingOrder.UNCLAIMED_AWAITING
                          ]
                              .includes(placingOrderType)
                              ? parse(t(`placingOrderModalWindow.description.${country}.${placingOrderType}`))
                              : undefined,
                  isNew,
                  regions: dataGetRegions?.provinces ?? [],
                  rememberAddress: true,
                  shipmentDates: {
                    values: [EPlacingOrder.FBP_NEW].includes(placingOrderType)
                        ? getShipmentDatesForCN([1, 2, 3])
                        : [EPlacingOrder.PICKUP_READY].includes(placingOrderType)
                            ? getShipmentDatesForCN(isNew ? [2, 3] : [0, 1, 2])
                            : [],
                    selected: [EPlacingOrder.FBP_NEW].includes(placingOrderType)
                        ? isNew
                            ? numberDays > 2
                                ? parcelsGoodTimeslotFrom
                                : undefined
                            : numberDays > 0
                                ? parcelsGoodTimeslotFrom
                                : undefined
                        : undefined
                  },
                  showComment: [EPlacingOrder.FBP_NEW, EPlacingOrder.PICKUP_READY].includes(placingOrderType) || [EPlacingOrder.UNCLAIMED_AWAITING].includes(placingOrderType) && conditionRU,
                  showEmail: [EPlacingOrder.UNCLAIMED_AWAITING].includes(placingOrderType) && conditionRU,
                  showOneCountryPhone: ([EPlacingOrder.UNCLAIMED_AWAITING].includes(placingOrderType) && conditionRU) || availableCountries?.length === 1,
                  title: t(`placingOrderModalWindow.headline.${country}.${placingOrderType}`),
                  values: undefined,
                  withDateButtons: [EPlacingOrder.FBP_NEW, EPlacingOrder.PICKUP_READY].includes(placingOrderType),
                  withDatePicker: [EPlacingOrder.FBP_NEW].includes(placingOrderType),
                  withFullAddressInEnglish: false
                }
              ]);
            });
        setForms(forms);
      }
    }
  }, [dataGetAddresses, dataGetRegions, deliveryType, parcelsGood]);

  const updateForm = (country: string, propertyPath: string, value: TUpdateFormValue): void => {
    const formIndex = forms.findIndex((form: TPlacingOrderForm) => form[0] === country);
    const formsModified: TPlacingOrderForm[] = forms.map((form: TPlacingOrderForm, index: number) => {
      if (index === formIndex) {
        const updatedData = { ...form[1] };
        const keys = propertyPath.split('.');
        let current: TPlacingOrderFormSelf = updatedData;
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          const nextValue = (current as any)[key];
          if (typeof nextValue === 'object' && nextValue !== null) {
            (current as any)[key] = { ...nextValue };
          } else {
            (current as any)[key] = {};
          }
          current = (current as any)[key];
        }
        (current as any)[keys[keys.length - 1]] = value;
        return [form[0], updatedData];
      } else {
        return form;
      }
    });
    setForms(formsModified);
  };

  const onClickConfirm = (): void => {
    const data: TPlacingOrderData = forms.map((item: TPlacingOrderForm) => {
      const formSelf: TPlacingOrderFormSelf = item[1];
      return ({
        address: formSelf.values!.address,
        ...(formSelf.values!.addressEnglish && ({ addressEnglish: formSelf.values!.addressEnglish })),
        ...(formSelf.values!.comment && ({ comment: formSelf.values!.comment })),
        ...(formSelf.values!.email && ({ email: formSelf.values!.email })),
        fullName: formSelf.values!.fullName,
        id: formSelf.values!.id,
        phone: formSelf.values!.phone,
        rememberAddress: formSelf.rememberAddress,
        ...(formSelf.shipmentDates.selected && ({ shipmentDate: formSelf.shipmentDates.selected }))
      });
    });
    handlerSubmit(data);
  };

  const onClickDeleteAddress = (id: string): void => {
    setAddressIdToDelete(id);
    setCurrentTypeModalWindow('delete');
  };

  const placingOrderViewData: TPlacingOrderViewData | undefined = useMemo(() => {
    const condition: boolean = account!.balance < costOfAction;
    if (forms.length) {
      return {
        ...(
            [
              EPlacingOrder.PROBLEMATIC_CUSTOMS,
              EPlacingOrder.PROBLEMATIC_FIRST_MILE,
              EPlacingOrder.RETURN_AWAITING,
              EPlacingOrder.UNCLAIMED_AWAITING
            ]
                .includes(placingOrderType)
                ?
                {
                  notice: {
                    title: t(`placingOrderModalWindow.order.notice.title.${placingOrderType}`)
                  }
                }
                : {}
        ),
        points: [
          {
            cols: [
              {
                title: [EPlacingOrder.FBP_NEW].includes(placingOrderType)
                    ? t('placingOrderModalWindow.order.point.col.fbp-services')
                    : t('placingOrderModalWindow.order.point.col.return-services'),
                tooltip: [EPlacingOrder.RETURN_AWAITING].includes(placingOrderType)
                    ? t(`placingOrderModalWindow.order.point.col.tooltip.${placingOrderType}`)
                    : undefined,
                value: formatSumWithCurrency(costOfAction, variableCurrency)
              },
              {
                color: condition ? TextColor.WARM_PINK : TextColor.PRIMARY,
                icon: condition ? WalletPinkIcon : WalletIcon,
                title: t('placingOrderModalWindow.order.point.col.balance'),
                value: formatSumWithCurrency(account!.balance, variableCurrency)
              },
              ...(condition
                  ? [
                    {
                      title: t('placingOrderModalWindow.order.point.col.notEnough'),
                      value: formatSumWithCurrency(Math.round((costOfAction - account!.balance) * 100) / 100, variableCurrency)
                    }
                  ]
                  : [])
            ],
            title: t('placingOrderModalWindow.order.point.title.cost')
          },
          ...forms.map((item: TPlacingOrderForm, index: number) => ({
                actionEdit: () => setCurrentStep(index + 1),
                rows: [
                  item[1].values?.address.rawLine || '',
                  item[1].values ? `${item[1].values.fullName}: ${item[1].values.phone}` : ''
                ],
                title: [
                  EPlacingOrder.FBP_NEW,
                  EPlacingOrder.PICKUP_READY
                ].includes(placingOrderType)
                    ? t('placingOrderModalWindow.order.point.title.loading-point')
                    : item[1].title
              }
          )),
          ...([EPlacingOrder.FBP_NEW].includes(placingOrderType) ? [
            {
              rows: [
                parcelsGoodWarehouse
              ],
              title: t('placingOrderModalWindow.order.point.title.warehouse')
            }
          ] : [])
        ],
        tabs: [
          {
            title: t(`placingOrderModalWindow.order.tab.${placingOrderType}.${expandTabs ? 'any' : 'good'}`),
            quantity: parcelsGood!.length
          },
          ...(parcelsAny
              ? [
                {
                  title: t(`placingOrderModalWindow.order.tab.${placingOrderType}.${expandTabs ? 'good' : 'any'}`),
                  quantity: parcelsAny.length
                }
              ]
              : [])
        ],
        title: t(`placingOrderModalWindow.headline.order.${placingOrderType}`)
      };
    }
  }, [forms, placingOrderType, costOfAction, account, parcelsGood, parcelsAny]);

  if (isLoadingGetAddresses || isLoadingGetRegions) {
    return (
        <Loader
            alignment="center"
            size="s"
        />
    );
  }

  if (placingOrderInProcessed) {
    return (
        <Grid>
          <PlacingOrderHeader title={t('placingOrderModalWindow.headline.placing')}/>
          <Loader
              alignment="center"
              size="xl"
          />
        </Grid>
    );
  }

  const isInvalidCountry = Boolean((placingOrderType === EPlacingOrder.RETURN_AWAITING) &&
    (availableCountries?.length === 1) &&
    (availableCountries?.[0] === 'RU') &&
    forms[0]?.[1]?.values?.address?.countryCode && (
    forms[0]?.[1]?.values?.address?.countryCode !== "RU"
  ));

  return (
      <>
        {currentTypeModalWindow === 'placing' && (
            currentStep > forms.length ? (
                <PlacingOrderView
                    buttons={[
                      {
                        action: () => {
                          handlerResetSendOrder();
                          setCurrentTypeModalWindow('cancel');
                        },
                        disabled: isLoadingSendPlacingOrder || isLoadingSendTopUpBalance,
                        text: t('placingOrderModalWindow.button.cancel'),
                        type: ButtonType.SKELETON
                      }, {
                        action: onClickConfirm,
                        disabled: isLoadingSendPlacingOrder || isLoadingSendTopUpBalance,
                        text: t(`placingOrderModalWindow.button.confirm.${placingOrderType}.${!!costOfAction ? 'forAFee' : 'forFree'}`),
                        type: ButtonType.BLOCK
                      }
                    ]}
                    currentStep={currentStep}
                    numberOfSteps={numberOfSteps}
                    parcelsAny={parcelsAny}
                    parcelsGood={parcelsGood!}
                    placingOrderViewData={placingOrderViewData!}
                />
            ) : (
                forms.map((item: TPlacingOrderForm, index: number) => {
                  const button: IPlacingOrderButton = {
                    action:
                        account?.region === 'TR'
                            ? index
                                ? () => setCurrentStep(currentStep - 1)
                                : () => setCurrentTypeModalWindow('cancel')
                            : placingOrderType === EPlacingOrder.UNCLAIMED_AWAITING
                                ? deliveryType === 'default' && !index
                                    ? () => setCurrentTypeModalWindow('selecting')
                                    : index
                                        ? () => setCurrentStep(currentStep - 1)
                                        : () => setCurrentTypeModalWindow('selecting')
                                : index
                                    ? () => setCurrentStep(currentStep - 1)
                                    : () => setCurrentTypeModalWindow('cancel'),

                    text:
                        account?.region === 'TR'
                            ? index
                                ? t('placingOrderModalWindow.button.back')
                                : t('placingOrderModalWindow.button.cancel')
                            : placingOrderType === EPlacingOrder.UNCLAIMED_AWAITING
                                ? t('placingOrderModalWindow.button.back')
                                : index
                                    ? t('placingOrderModalWindow.button.back')
                                    : t('placingOrderModalWindow.button.cancel'),

                    type: ButtonType.SKELETON
                  };
                  return currentStep === index + 1 && (
                      <PlacingOrderForm
                          buttons={[
                            button,
                            {
                              action: () => {
                                resetGetValidation();
                                setCurrentStep(currentStep + 1);
                              },
                              disabled: isLoadingGetValidation || isInvalidCountry,
                              text: t('placingOrderModalWindow.button.next'),
                              type: ButtonType.BLOCK
                            }
                          ]}
                          country={item[0]}
                          currentStep={currentStep}
                          dataGetValidation={dataGetValidation}
                          handlerMutateGetValidation={mutateGetValidation}
                          handlerOnClickDeleteAddress={onClickDeleteAddress}
                          handlerUpdateForm={updateForm}
                          key={index}
                          numberOfSteps={numberOfSteps}
                          {...item[1]}
                      />
                  );
                })
            )
        )}
        {currentTypeModalWindow === 'cancel' && (
            <PlacingOrderAction
                buttons={[
                  {
                    action: () => setCurrentTypeModalWindow('placing'),
                    text: t('placingOrderModalWindow.button.no'),
                    type: ButtonType.SKELETON
                  },
                  {
                    action: handlerOnClosePlacingOrderProcessing,
                    text: t('placingOrderModalWindow.button.cancel'),
                    type: ButtonType.DANGER
                  }
                ]}
                description={t(`placingOrderModalWindow.description.cancel.${placingOrderType}`)}
                title={t(`placingOrderModalWindow.headline.cancel.${placingOrderType}`)}
            />
        )}
        {currentTypeModalWindow === 'delete' && (
            <PlacingOrderAction
                buttons={[
                  {
                    action: () => {
                      resetDeleteAddress();
                      setCurrentTypeModalWindow('placing');
                    },
                    text: t('placingOrderModalWindow.button.no'),
                    type: ButtonType.SKELETON
                  },
                  {
                    action: () => mutateDeleteAddress(addressIdToDelete),
                    pending: isLoadingDeleteAddress,
                    text: t('placingOrderModalWindow.button.delete'),
                    type: ButtonType.DANGER
                  }
                ]}
                description={t('placingOrderModalWindow.description.delete')}
                title={t('placingOrderModalWindow.headline.delete')}
            />
        )}
        {currentTypeModalWindow === 'selecting' && (
            <PlacingOrderSelectingDelivery
                buttons={[
                  {
                    action: handlerOnClosePlacingOrderProcessing,
                    text: t('placingOrderModalWindow.button.cancel'),
                    type: ButtonType.SKELETON
                  },
                  {
                    action: () => setCurrentTypeModalWindow('placing'),
                    disabled: !deliveryType!.length,
                    text: t('placingOrderModalWindow.button.next'),
                    type: ButtonType.BLOCK
                  }
                ]}
                costOfService={costOfService}
                costOfServiceDifferent={costOfServiceDifferent!}
                deliveryType={deliveryType!}
                handlerSetDeliveryType={handlerSetDeliveryType!}
                allowRu={allowRu}
            />
        )}
        {isErrorDeleteAddress && <FormErrorMessage error={errorDeleteAddress}/>}
        {isErrorSendPlacingOrder && <FormErrorMessage error={errorSendPlacingOrder}/>}
        {isErrorSendTopUpBalance && <FormErrorMessage error={errorSendTopUpBalance} />}
        {isInvalidCountry && <FormErrorMessage error={errorSendTopUpBalance} />}
      </>
  );
};

export default PlacingOrderModalWindow;
